import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../shared/DashboardLayout';
import LeaveManagementSidebar from '../leave/LeaveManagementSidebar';
import Calendar from '../shared/Calendar';
import { driverAPI, leaveAPI } from '../../services/api';
import { notify, confirmDialog } from '../../utils/notify';
import '../../styles/Dashboard.css';
import '../../styles/DriverDashboard.css';

const VEHICLE_TYPE_LIST = [
  { id: 'BUS', label: 'Bus', icon: '🚌', desc: 'Heavy Passenger Vehicle (30+ Capacity)' },
  { id: 'VAN', label: 'Van / Minibus', icon: '🚐', desc: 'Medium Transport (10-25 Capacity)' },
  { id: 'CAR', label: 'Car / Utility', icon: '🚗', desc: 'Light Motor Vehicle (4-7 Capacity)' },
  { id: 'AMBULANCE', label: 'Ambulance', icon: '🚑', desc: 'Emergency Medical Service Vehicle' },
  { id: 'OTHER', label: 'Other', icon: '🚜', desc: 'Specialized Institution Vehicles' },
];

const DriverDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('documents');

  // File Input Refs
  const licenceFileInputRef = useRef(null);
  const medicalFileInputRef = useRef(null);

  // Profile & State
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // File names for display
  const [licenceFileName, setLicenceFileName] = useState('');
  const [medicalFileName, setMedicalFileName] = useState('');

  // Leave State
  const [myLeaves, setMyLeaves] = useState([]);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ leave_type: 'CL', from_date: '', to_date: '', reason: '' });

  // Form States (Independent to avoid resetting each other)
  const [licenceForm, setLicenceForm] = useState({
    licence_number: '',
    licence_category: 'LMV/HMV',
    licence_issue_date: '',
    licence_expiry_date: '',
    licence_document: '',
  });

  const [medicalForm, setMedicalForm] = useState({
    medical_issue_date: '',
    medical_expiry_date: '',
    medical_document: '',
  });

  // Vehicle Types State
  const [selectedVehicleTypes, setSelectedVehicleTypes] = useState([]);

  // Calculated Submitted Statuses (Read-Only until verification/rejection)
  const isLicenceSubmitted = ['PENDING_VERIFICATION', 'VERIFIED', 'UPLOADED'].includes(
    profile?.licence_verification_status
  );
  const isMedicalSubmitted = ['PENDING_VERIFICATION', 'VERIFIED', 'UPLOADED'].includes(
    profile?.medical_verification_status
  );

  // Fetch Profile
  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await driverAPI.getProfile();
      setProfile(data);

      // Populate Licence form from server
      setLicenceForm(prev => ({
        licence_number: data.licence_number || prev.licence_number || '',
        licence_category: data.licence_category || prev.licence_category || 'LMV/HMV',
        licence_issue_date: data.licence_issue_date || prev.licence_issue_date || '',
        licence_expiry_date: data.licence_expiry_date || prev.licence_expiry_date || '',
        licence_document: data.licence_document || prev.licence_document || '',
      }));

      // Populate Medical form from server
      setMedicalForm(prev => ({
        medical_issue_date: data.medical_issue_date || prev.medical_issue_date || '',
        medical_expiry_date: data.medical_expiry_date || prev.medical_expiry_date || '',
        medical_document: data.medical_document || prev.medical_document || '',
      }));

      setSelectedVehicleTypes(data.permitted_vehicle_types || []);
    } catch (err) {
      console.error('Failed to load driver profile:', err);
      setError(err.response?.data?.error || 'Failed to load driver profile.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLeaves = useCallback(async () => {
    try {
      if (leaveAPI && leaveAPI.getMyLeaves) {
        const data = await leaveAPI.getMyLeaves();
        setMyLeaves(Array.isArray(data) ? data : data.results || []);
      }
    } catch (err) {
      console.error('Failed to load leave history:', err);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
    fetchLeaves();
  }, [fetchProfile, fetchLeaves]);

  // File Upload Handler
  const handleFileUpload = (e, callback, setFileName) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      notify.success('File size exceeds 5MB limit.');
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      callback(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Submit Driving Licence
  const handleSaveLicence = async (e) => {
    if (e) e.preventDefault();
    if (isLicenceSubmitted) return;

    if (!licenceForm.licence_number || !licenceForm.licence_expiry_date) {
      setError('Please fill in Licence Number and Expiry Date before submitting.');
      return;
    }
    if (!licenceForm.licence_document) {
      // If no file attached yet, trigger file picker
      if (licenceFileInputRef.current) licenceFileInputRef.current.click();
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccessMsg(null);
      const updatedData = await driverAPI.updateProfile({
        licence_number: licenceForm.licence_number,
        licence_category: licenceForm.licence_category,
        licence_issue_date: licenceForm.licence_issue_date || null,
        licence_expiry_date: licenceForm.licence_expiry_date || null,
        licence_document: licenceForm.licence_document,
      });
      setSuccessMsg('Driving licence details submitted successfully for verification.');
      setProfile(updatedData);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to submit driving licence details.');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit Medical Fitness Certificate
  const handleSaveMedical = async (e) => {
    if (e) e.preventDefault();
    if (isMedicalSubmitted) return;

    if (!medicalForm.medical_expiry_date) {
      setError('Please fill in Medical Certificate Expiry Date before submitting.');
      return;
    }
    if (!medicalForm.medical_document) {
      // If no file attached yet, trigger file picker
      if (medicalFileInputRef.current) medicalFileInputRef.current.click();
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccessMsg(null);
      const updatedData = await driverAPI.updateProfile({
        medical_issue_date: medicalForm.medical_issue_date || null,
        medical_expiry_date: medicalForm.medical_expiry_date || null,
        medical_document: medicalForm.medical_document,
      });
      setSuccessMsg('Medical Fitness certificate submitted successfully for verification.');
      setProfile(updatedData);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to submit medical fitness certificate.');
    } finally {
      setSubmitting(false);
    }
  };

  // Save Vehicle Types Request
  const handleSaveVehicleTypes = async () => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccessMsg(null);
      const updatedData = await driverAPI.updateProfile({ permitted_vehicle_types: selectedVehicleTypes });
      setSuccessMsg('Permitted vehicle types updated successfully.');
      setProfile(updatedData);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to update vehicle types.');
    } finally {
      setSubmitting(false);
    }
  };

  // Apply Leave Submit
  const handleApplyLeave = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);
      if (leaveAPI && leaveAPI.applyLeave) {
        await leaveAPI.applyLeave(leaveForm);
        setSuccessMsg('Leave application submitted successfully.');
        setShowLeaveModal(false);
        setLeaveForm({ leave_type: 'CL', from_date: '', to_date: '', reason: '' });
        fetchLeaves();
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to apply for leave.');
    } finally {
      setSubmitting(false);
    }
  };

  // Badge Helper
  const getVerificationBadge = (status, rejectionReason) => {
    switch (status) {
      case 'VERIFIED':
        return <span className="badge-status verified">✓ Verified</span>;
      case 'PENDING_VERIFICATION':
        return <span className="badge-status pending">⏳ Pending Verification</span>;
      case 'REJECTED':
        return (
          <span className="badge-status rejected" title={rejectionReason}>
            ✕ Rejected {rejectionReason ? `(${rejectionReason})` : ''}
          </span>
        );
      case 'UPLOADED':
        return <span className="badge-status pending">✓ Uploaded</span>;
      default:
        return <span className="badge-status unuploaded">Not Uploaded</span>;
    }
  };

  const el = profile?.eligibility_details || { is_eligible: false, reasons: [] };

  return (
    <DashboardLayout fullWidth>
      <div className="driver-portal-container">
        {/* Top Header Card */}
        <div className="driver-header-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 800, boxShadow: '0 4px 10px rgba(37,99,235,0.25)' }}>
              {user?.first_name ? user.first_name[0].toUpperCase() : 'D'}
            </div>
            <div>
              <h1 className="driver-welcome-title">
                Welcome, {user?.first_name ? `${user.first_name} ${user.last_name || ''}` : user?.username}!
              </h1>
              <div className="driver-welcome-subtitle">
                <span>Employee ID:</span>
                <span className="driver-id-chip">{profile?.employee_id || 'DRV260001'}</span>
                <span>&bull; Role: <strong>Driver</strong></span>
              </div>
            </div>
          </div>

          <div>
            {el.is_eligible ? (
              <div className="driver-eligibility-badge eligible">
                <span className="status-indicator-dot"></span>
                <span>ELIGIBLE FOR VEHICLE ASSIGNMENT</span>
              </div>
            ) : (
              <div className="driver-eligibility-badge not-eligible">
                <span className="status-indicator-dot"></span>
                <span>NOT ELIGIBLE FOR ASSIGNMENT</span>
              </div>
            )}
          </div>
        </div>

        {/* Global Notifications */}
        {error && (
          <div className="alert alert-danger" style={{ marginBottom: 20, borderRadius: 12, padding: '14px 18px', background: '#fff1f2', border: '1px solid #fecdd3', color: '#9f1239', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>⚠️</span> <span>{error}</span>
          </div>
        )}
        {successMsg && (
          <div className="alert alert-success" style={{ marginBottom: 20, borderRadius: 12, padding: '14px 18px', background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>✓</span> <span>{successMsg}</span>
          </div>
        )}

        {/* Profile Completion Warning Banner - Hidden when 100% */}
        {profile && Number(profile.profile_completion_percentage) < 100 && (
          <div className="driver-progress-card">
            <div className="driver-progress-header">
              <h3 className="driver-progress-title">
                <span>⚠️</span> Driver Professional Profile Setup
              </h3>
              <span className="driver-progress-pct">
                Profile Completion: {profile.profile_completion_percentage}%
              </span>
            </div>
            <div className="driver-progress-bar-bg">
              <div className="driver-progress-bar-fill" style={{ width: `${profile.profile_completion_percentage}%` }} />
            </div>

            <div className="driver-steps-grid">
              <div className={`driver-step-item ${profile.approval_status === 'APPROVED' ? 'done' : 'pending'}`}>
                {profile.approval_status === 'APPROVED' ? '✓' : '⏳'} 1. Account Approved by Admin
              </div>
              <div className={`driver-step-item ${profile.licence_verification_status === 'VERIFIED' ? 'done' : 'pending'}`}>
                {profile.licence_verification_status === 'VERIFIED' ? '✓' : '⏳'} 2. Driving Licence Verified
              </div>
              <div className={`driver-step-item ${profile.medical_verification_status === 'VERIFIED' ? 'done' : 'pending'}`}>
                {profile.medical_verification_status === 'VERIFIED' ? '✓' : '⏳'} 3. Medical Fitness Verified
              </div>
              <div className={`driver-step-item ${profile.permitted_vehicle_types && profile.permitted_vehicle_types.length > 0 ? 'done' : 'pending'}`}>
                {profile.permitted_vehicle_types && profile.permitted_vehicle_types.length > 0 ? '✓' : '⏳'} 4. Vehicle Types Approved
              </div>
            </div>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="driver-nav-tabs">
          <button className={`driver-tab-btn ${activeTab === 'documents' ? 'active' : ''}`} onClick={() => setActiveTab('documents')}>
            <span>📄</span> Driver Documents
          </button>
          <button className={`driver-tab-btn ${activeTab === 'vehicle_types' ? 'active' : ''}`} onClick={() => setActiveTab('vehicle_types')}>
            <span>🚍</span> Permitted Vehicle Types
          </button>
          <button className={`driver-tab-btn ${activeTab === 'assigned_vehicle' ? 'active' : ''}`} onClick={() => setActiveTab('assigned_vehicle')}>
            <span>🚘</span> My Assigned Vehicle
          </button>
          <button className={`driver-tab-btn ${activeTab === 'leave' ? 'active' : ''}`} onClick={() => setActiveTab('leave')}>
            <span>🌴</span> My Leave
          </button>
          <button className={`driver-tab-btn ${activeTab === 'calendar' ? 'active' : ''}`} onClick={() => setActiveTab('calendar')}>
            <span>📅</span> Calendar
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#64748b', background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>🔄</div>
            <div>Loading Driver Portal...</div>
          </div>
        ) : (
          <>
            {/* DRIVER DOCUMENTS TAB */}
            {activeTab === 'documents' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 24, alignItems: 'stretch' }}>
                
                {/* ── DRIVING LICENCE FORM CARD ── */}
                <div className="driver-card">
                  <div className="driver-card-header">
                    <h3 className="driver-card-title">
                      <span>🪪</span> Driving Licence
                    </h3>
                    {getVerificationBadge(profile?.licence_verification_status, profile?.licence_rejection_reason)}
                  </div>

                  {/* Hidden File Picker Input */}
                  <input
                    ref={licenceFileInputRef}
                    type="file"
                    disabled={isLicenceSubmitted}
                    accept="image/*,application/pdf"
                    onChange={e => handleFileUpload(e, base64 => setLicenceForm(prev => ({ ...prev, licence_document: base64 })), setLicenceFileName)}
                    style={{ display: 'none' }}
                  />

                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <div className="driver-form-group">
                      <label className="driver-label">Licence Number *</label>
                      <input
                        type="text"
                        required
                        disabled={isLicenceSubmitted}
                        readOnly={isLicenceSubmitted}
                        placeholder="e.g. DL-1420110012345"
                        value={licenceForm.licence_number}
                        onChange={e => setLicenceForm({ ...licenceForm, licence_number: e.target.value.toUpperCase() })}
                        className="driver-input"
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }} className="driver-form-group">
                      <div>
                        <label className="driver-label">Issue Date</label>
                        <input
                          type="date"
                          disabled={isLicenceSubmitted}
                          readOnly={isLicenceSubmitted}
                          value={licenceForm.licence_issue_date}
                          onChange={e => setLicenceForm({ ...licenceForm, licence_issue_date: e.target.value })}
                          className="driver-input"
                        />
                      </div>
                      <div>
                        <label className="driver-label">Expiry Date *</label>
                        <input
                          type="date"
                          required
                          disabled={isLicenceSubmitted}
                          readOnly={isLicenceSubmitted}
                          value={licenceForm.licence_expiry_date}
                          onChange={e => setLicenceForm({ ...licenceForm, licence_expiry_date: e.target.value })}
                          className="driver-input"
                        />
                      </div>
                    </div>

                    <div className="driver-form-group">
                      <label className="driver-label">Licence Document File *</label>
                      <div
                        className={`driver-file-dropzone ${licenceForm.licence_document ? 'has-file' : ''} ${isLicenceSubmitted ? 'disabled' : ''}`}
                        onClick={() => {
                          if (!isLicenceSubmitted && licenceFileInputRef.current) {
                            licenceFileInputRef.current.click();
                          }
                        }}
                      >
                        <div className="dropzone-icon">{isLicenceSubmitted ? '🔒' : licenceForm.licence_document ? '✅' : '📁'}</div>
                        <div className="dropzone-text">
                          {licenceFileName
                            ? licenceFileName
                            : licenceForm.licence_document
                            ? 'Licence File Attached'
                            : isLicenceSubmitted
                            ? 'Licence Document Submitted'
                            : 'Click to Choose Licence File'}
                        </div>
                        <div className="dropzone-subtext">
                          {isLicenceSubmitted
                            ? 'Document locked until verification is completed'
                            : 'Supports PNG, JPG, PDF (Max 5MB)'}
                        </div>
                      </div>
                    </div>

                    {/* Dynamic Action Button */}
                    {isLicenceSubmitted ? (
                      <button type="button" disabled className="driver-btn-submitted">
                        {profile?.licence_verification_status === 'VERIFIED'
                          ? '✓ Driving Licence Verified'
                          : '⏳ Licence Submitted (Pending Verification)'}
                      </button>
                    ) : !licenceForm.licence_document ? (
                      <button
                        type="button"
                        onClick={() => licenceFileInputRef.current && licenceFileInputRef.current.click()}
                        className="driver-btn-upload"
                      >
                        📁 Upload Licence
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSaveLicence}
                        disabled={submitting}
                        className="driver-btn-primary"
                      >
                        {submitting ? 'Submitting...' : 'Submit Licence'}
                      </button>
                    )}
                  </div>
                </div>

                {/* ── MEDICAL FITNESS CERTIFICATE FORM CARD ── */}
                <div className="driver-card">
                  <div className="driver-card-header">
                    <h3 className="driver-card-title">
                      <span>🩺</span> Medical Fitness Certificate
                    </h3>
                    {getVerificationBadge(profile?.medical_verification_status, profile?.medical_rejection_reason)}
                  </div>

                  {/* Hidden File Picker Input */}
                  <input
                    ref={medicalFileInputRef}
                    type="file"
                    disabled={isMedicalSubmitted}
                    accept="image/*,application/pdf"
                    onChange={e => handleFileUpload(e, base64 => setMedicalForm(prev => ({ ...prev, medical_document: base64 })), setMedicalFileName)}
                    style={{ display: 'none' }}
                  />

                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }} className="driver-form-group">
                      <div>
                        <label className="driver-label">Issue Date</label>
                        <input
                          type="date"
                          disabled={isMedicalSubmitted}
                          readOnly={isMedicalSubmitted}
                          value={medicalForm.medical_issue_date}
                          onChange={e => setMedicalForm({ ...medicalForm, medical_issue_date: e.target.value })}
                          className="driver-input"
                        />
                      </div>
                      <div>
                        <label className="driver-label">Expiry Date *</label>
                        <input
                          type="date"
                          required
                          disabled={isMedicalSubmitted}
                          readOnly={isMedicalSubmitted}
                          value={medicalForm.medical_expiry_date}
                          onChange={e => setMedicalForm({ ...medicalForm, medical_expiry_date: e.target.value })}
                          className="driver-input"
                        />
                      </div>
                    </div>

                    <div className="driver-form-group">
                      <label className="driver-label">Certificate Document File *</label>
                      <div
                        className={`driver-file-dropzone ${medicalForm.medical_document ? 'has-file' : ''} ${isMedicalSubmitted ? 'disabled' : ''}`}
                        onClick={() => {
                          if (!isMedicalSubmitted && medicalFileInputRef.current) {
                            medicalFileInputRef.current.click();
                          }
                        }}
                      >
                        <div className="dropzone-icon">{isMedicalSubmitted ? '🔒' : medicalForm.medical_document ? '✅' : '📁'}</div>
                        <div className="dropzone-text">
                          {medicalFileName
                            ? medicalFileName
                            : medicalForm.medical_document
                            ? 'Medical Certificate Attached'
                            : isMedicalSubmitted
                            ? 'Medical Certificate Submitted'
                            : 'Click to Choose Medical File'}
                        </div>
                        <div className="dropzone-subtext">
                          {isMedicalSubmitted
                            ? 'Document locked until verification is completed'
                            : 'Supports PNG, JPG, PDF (Max 5MB)'}
                        </div>
                      </div>
                    </div>

                    {/* Dynamic Action Button */}
                    {isMedicalSubmitted ? (
                      <button type="button" disabled className="driver-btn-submitted">
                        {profile?.medical_verification_status === 'VERIFIED'
                          ? '✓ Medical Certificate Verified'
                          : '⏳ Certificate Submitted (Pending Verification)'}
                      </button>
                    ) : !medicalForm.medical_document ? (
                      <button
                        type="button"
                        onClick={() => medicalFileInputRef.current && medicalFileInputRef.current.click()}
                        className="driver-btn-upload"
                      >
                        📁 Upload Medical Certificate
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSaveMedical}
                        disabled={submitting}
                        className="driver-btn-primary"
                      >
                        {submitting ? 'Submitting...' : 'Submit Medical Certificate'}
                      </button>
                    )}
                  </div>
                </div>

              </div>
            )}

            {/* PERMITTED VEHICLE TYPES TAB */}
            {activeTab === 'vehicle_types' && (
              <div className="driver-card">
                <div className="driver-card-header">
                  <h3 className="driver-card-title">
                    <span>🚍</span> Vehicle Categories I Can Drive
                  </h3>
                </div>
                <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
                  Select the vehicle categories authorized on your official driving licence. The Transport Manager will verify these selections against your uploaded licence.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
                  {VEHICLE_TYPE_LIST.map(vt => {
                    const isChecked = selectedVehicleTypes.includes(vt.id);
                    return (
                      <div
                        key={vt.id}
                        className={`vehicle-choice-card ${isChecked ? 'selected' : ''}`}
                        onClick={() => {
                          if (isChecked) {
                            setSelectedVehicleTypes(selectedVehicleTypes.filter(x => x !== vt.id));
                          } else {
                            setSelectedVehicleTypes([...selectedVehicleTypes, vt.id]);
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {}}
                          style={{ width: 18, height: 18, accentColor: '#2563eb' }}
                        />
                        <span style={{ fontSize: 24 }}>{vt.icon}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{vt.label}</div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>{vt.desc}</div>
                        </div>
                        {isChecked && <span style={{ color: '#2563eb', fontWeight: 800 }}>✓ Selected</span>}
                      </div>
                    );
                  })}
                </div>

                <button
                  onClick={handleSaveVehicleTypes}
                  disabled={submitting}
                  className="driver-btn-primary"
                  style={{ width: 'auto', alignSelf: 'flex-start' }}
                >
                  Save Vehicle Types Request
                </button>
              </div>
            )}

            {/* MY ASSIGNED VEHICLE TAB */}
            {activeTab === 'assigned_vehicle' && (
              <div className="driver-card">
                <div className="driver-card-header">
                  <h3 className="driver-card-title">
                    <span>🚘</span> Currently Assigned Institution Vehicle
                  </h3>
                </div>
                {profile?.assigned_vehicle ? (
                  <div style={{ background: '#f8fafc', borderRadius: 16, padding: 24, border: '1px solid #cbd5e1' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                      <div className="license-plate-badge">
                        {profile.assigned_vehicle.registration_number}
                      </div>
                      <span className="badge-status verified" style={{ fontSize: 12, padding: '6px 14px' }}>✓ Active Duty</span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, fontSize: 13 }}>
                      <div style={{ background: '#fff', padding: 14, borderRadius: 10, border: '1px solid #e2e8f0' }}>
                        <div style={{ color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Vehicle Type</div>
                        <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>{profile.assigned_vehicle.vehicle_type_display}</div>
                      </div>
                      <div style={{ background: '#fff', padding: 14, borderRadius: 10, border: '1px solid #e2e8f0' }}>
                        <div style={{ color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Make & Model</div>
                        <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>{profile.assigned_vehicle.make} {profile.assigned_vehicle.model}</div>
                      </div>
                      <div style={{ background: '#fff', padding: 14, borderRadius: 10, border: '1px solid #e2e8f0' }}>
                        <div style={{ color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Seating Capacity</div>
                        <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>{profile.assigned_vehicle.seating_capacity} Passenger Seats</div>
                      </div>
                      <div style={{ background: '#fff', padding: 14, borderRadius: 10, border: '1px solid #e2e8f0' }}>
                        <div style={{ color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Assigned Date</div>
                        <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>{new Date(profile.assigned_vehicle.assigned_at).toLocaleDateString('en-IN')}</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: 40, textAlign: 'center', color: '#64748b', background: '#f8fafc', borderRadius: 12, border: '1px dashed #cbd5e1' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>🚍</div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#334155' }}>No Vehicle Currently Assigned</div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>The Transport Manager will assign a vehicle once your professional profile and documents are verified.</div>
                  </div>
                )}
              </div>
            )}

            {/* MY LEAVE TAB */}
            {activeTab === 'leave' && (
              <LeaveManagementSidebar />
            )}

            {/* CALENDAR TAB */}
            {activeTab === 'calendar' && (
              <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 20 }}>
                <Calendar />
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DriverDashboard;
